import React, { useState, useEffect } from "react"
//import styled from 'styled-components'
//import breakpoint from 'styled-components-breakpoint'
import { Link } from "gatsby"
import tw, { styled } from "twin.macro"

import { SearchRounded, CloseRounded } from "@mui/icons-material"

import sw from "stopword"

const Search = ({ ...props }) => {
  const [searchKeywords, setSearchKeywords] = useState(
    props.keywords && props.keywords.split(" ")
  )
  const [searchKeywordsRaw, setSearchKeywordsRaw] = useState(
    props.keywords || ""
  )
  const [results, setResults] = useState([])
  const [submitted, setSubmitted] = useState(false)

  const input = React.createRef()

  const submit = (event) => {
    event.preventDefault()

    if (!searchKeywords.length || !window.__LUNR__) return []

    const lunrIndex = window.__LUNR__["en"]

    const found = lunrIndex.index.search(searchKeywords.join(" "))
    setResults(
      found
        .map(({ ref }) => lunrIndex.store[ref])
        .filter((f) => f.url !== null && f.title !== "")
    )
    props.callback(true)
    setSubmitted(true)
    input.current.blur()

    window.dataLayer = window.dataLayer || []
    window.dataLayer.push({
      event: "Search",
      searchTerm: searchKeywords.join(" "),
    })
  }

  const reset = (event) => {
    event.preventDefault()
    setSearchKeywords("")

    setResults([])
    setSubmitted(false)
    props.callback(false)
    input.current.blur()
  }

  useEffect(() => {
    // autosearch on mount
    if (props.keywords) {
      const lunrIndex = window.__LUNR__["en"]
      const found = lunrIndex.index.search(props.keywords.join(" "))
      setResults(
        found
          .map(({ ref }) => lunrIndex.store[ref])
          .filter((f) => f.url !== null && f.title !== "")
      )
    }
  }, [props.keywords])

  return (
    <Style className="search" role="search">
      <form
        onSubmit={(event) => submit(event)}
        onReset={(event) => reset(event)}
      >
        <input
          type="text"
          name="search"
          id="search"
          inputMode="search"
          value={searchKeywordsRaw}
          required
          ref={input}
          autoComplete={"off"}
          autoCapitalize={"off"}
          autoCorrect={"off"}
          onChange={(event) => {
            setSearchKeywordsRaw(event.target.value)
            setSearchKeywords(sw.removeStopwords(event.target.value.split(" ")))
          }}
        />
        <label htmlFor="search">Enter keywords&hellip;</label>
        {results.length === 0 ? (
          <button type="submit" aria-label="Submit search">
            <SearchRounded />
          </button>
        ) : null}
        {results.length > 0 ? (
          <button type="reset" aria-label="Close search">
            <CloseRounded />
          </button>
        ) : null}
      </form>

      {results.length > 0 && (
        <ResultStyle className="search__results">
          <p aria-live="polite">
            Found {results.length} result{results.length === 1 ? "" : "s"}
          </p>
          <ul className="search__results">
            {results.map((result, i) => (
              <li key={i} style={{ animationDelay: `${(i + 1) / 10}s` }}>
                <Link to={result.url}>
                  {result.title}
                  <small>{result.url}</small>
                </Link>
              </li>
            ))}
          </ul>
        </ResultStyle>
      )}

      {results.length === 0 && submitted === true && (
        <ResultStyle className="search__results">
          <p>No results found</p>
        </ResultStyle>
      )}
    </Style>
  )
}

Search.defaultProps = {
  callback: () => {},
}

export default Search

const Style = styled.div`
  ${tw`w-full`}

  --button-size: 50px;

  .tray & {
    ${tw`[padding:2vmax]`}

    --button-size: 40px;
  }

  * + & {
    ${tw`[marginTop:2vh]`}
  }

  form {
    display: grid;
    grid-template-rows: 1fr;
    grid-template-columns: 1fr auto;
    border-bottom: 1px solid rgba(0, 0, 0, 0.35);
    margin-bottom: 0;
    ${tw`mb-0`}

    .tray & {
      ${tw`text-white`}
      border-bottom: 1px solid rgba(255,255,255,.35);
    }

    input,
    label {
      grid-row: 1 / 2;
      grid-column: 1 / 2;
      padding: 0 2vmax;
      height: var(--button-size);
      line-height: var(--button-size);
      font-size: 15px;
    }

    input {
      ${tw`bg-transparent text-gray-900 outline-none border-none text-lg`}
      box-shadow: none;
      appearance: none;
      border-radius: 0;
      z-index: 10;
      padding-left: 0;

      &:focus + label {
        opacity: 0;
      }

      .tray & {
        ${tw`text-white text-lg`}
      }
    }

    label {
      opacity: 0;
      z-index: 1;
      padding-left: 0;
    }

    input[value=""] + label {
      opacity: 0.45;
    }

    button {
      grid-row: 1 / 2;
      grid-column: 2 / 3;
      appearance: none;
      background-color: transparent;
      border: none;
      width: var(--button-size);
      height: var(--button-size);
      display: flex;
      align-items: center;
      justify-content: center;
      ${tw`text-gray-900 cursor-pointer outline-none focus-visible:text-orange! hover:text-primary`}

      .tray & {
        ${tw`text-white`}

        > svg {
          ${tw`h-3/4 w-3/4`}
        }
      }

      > svg {
        ${tw`w-full h-full`}
      }
    }
  }
`

const ResultStyle = styled.output`
  margin-top: 2vh;
  height: auto;
  overflow-y: auto;
  ${tw`block h-auto`}

  .tray & {
    height: 88vh;
  }

  > p {
    margin-bottom: 0;
    padding: 0.5em 0;
    ${tw`text-gray-900 uppercase text-xs`}

    .tray & {
      ${tw`text-white mt-4`}
    }
  }

  > ul {
    font-size: calc(0.35em + 2.5vmax);
    overflow-y: auto;
    ${tw`md:text-lg font-medium m-0 p-0 list-none text-lg`}

    a {
      ${tw`no-underline w-full font-semibold antialiased block overflow-hidden text-gray-900 truncate hover:text-primary focus-visible:text-orange`}
      padding: .45em 0;
      border-top: 1px solid rgba(255, 255, 255, 0.25);
      line-height: 1.35;

      .tray & {
        ${tw`text-white`}
      }

      &[aria-current="page"] {
        ${tw`bg-green hover:text-white focus-visible:text-white cursor-default`}
        padding-left: 2vmax;
      }

      small {
        font-size: 50%;
        font-weight: 500;
        opacity: 0.5;
        font-size: 13px;
        ${tw`block overflow-hidden truncate font-normal`}
      }
    }

    > li {
      ${tw`mb-0`}
    }
  }
`

/*getSearchResults(query) {
        if (!query || !window.__LUNR__) return []
        const lunrIndex =  window.__LUNR__[this.props.lng];
        const results = lunrIndex.index.search(query) // you can  customize your search , see https://lunrjs.com/guides/searching.html
        return results.map(({ ref }) => lunrIndex.store[ref])
    }

    search = event => {
        const query = event.target.value
        const results = this.getSearchResults(query)
        this.setState(s => {
            return {
                results,
                query,
            }
        })
    }*/
